import { useCallback } from 'react';

import { type Client, createRequest, useClient } from 'urql';

import { type AddLineItemToCartInput, type DeliveryOrderDetailInput } from '@sg/graphql-schema';
import { type CustomizationMachineTypes } from '@sg/shared-fe-features-customization';

import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { useCart, useGroupOrderTelemetry } from '../../../../hooks';
import {
  AddLineItemToCartDocument,
  type AddLineItemToCartMutation,
  type AddLineItemToCartMutationVariables,
} from '../../../../hooks/useCart/GraphQL/Cart.generated';
import { extractIngredientId, extractSubstitution } from '../../../../hooks/useCart/useCart.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adds one bundle product to the bag, and throws when it is not added. Unlike a
 * single add, it shows no notice and plays no haptic, since the bundle reports
 * once for all of its products.
 */
export const useAddBundleProductToBag = () => {
  const client = useClient();
  const { executeCartMutation, isGroupOrder } = useCart();
  const { track } = useTelemetry();
  const { trackItemActionFailure } = useGroupOrderTelemetry();

  return useCallback(
    async (params: Params) => {
      const { product } = params;

      if (!product) {
        throw new Error('The bundle product has not loaded.');
      }

      const input = getAddLineItemToCartInput({ ...params, product });
      const response = await executeCartMutation(
        async () => addLineItemToCart({ client, input }),
        'add-line-item',
      );
      const result = response.data?.addLineItemToCart;

      if (result?.__typename === 'AddLineItemToCartSuccess') return;

      const isBlockedByGroupOrder = isGroupOrder && result?.__typename === 'OrderLocked';
      const possibleError = result as Readonly<{ message?: string }> | undefined;

      if (isBlockedByGroupOrder) {
        trackItemActionFailure();
      }

      track('bundle.product.add_to_bag.failed', {
        productId: product.id,
        userError: possibleError?.message,
        systemError: getUrqlError(response.error),
      });

      throw new Error('The bundle product was not added.');
    },
    [client, executeCartMutation, isGroupOrder, track, trackItemActionFailure],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAddLineItemToCartInput(
  params: GetAddLineItemToCartInputParams,
): AddLineItemToCartInput {
  const {
    product,
    quantity,
    additions,
    removals,
    substitutions,
    mixedDressingDetails,
    deliveryDetails,
  } = params;

  return {
    productId: product.id,
    customName: null,
    quantity,
    mixedDressingDetails,
    additions: additions.map(extractIngredientId),
    removals: removals.map(extractIngredientId),
    substitutions: substitutions.map(extractSubstitution),
    ...(deliveryDetails ? { deliveryOrderDetails: deliveryDetails } : {}),
  };
}

/**
 * Sends the add without the notices of the shared mutation hook. Resolves with
 * any error rather than rejecting, which keeps the cart's pending mutation count
 * balanced.
 */
function addLineItemToCart(params: AddLineItemToCartParams) {
  const { client, input } = params;

  const request = createRequest<AddLineItemToCartMutation, AddLineItemToCartMutationVariables>(
    AddLineItemToCartDocument,
    { input },
  );

  // NOTE: urql keys requests by an integer hash of the query and variables, and the
  //       auth exchange keeps one operation per key while it refreshes the token, so a
  //       repeated product would be dropped. A random fraction never matches a hash.
  return client
    .executeMutation({ ...request, key: Math.random() }, { requestPolicy: 'network-only' })
    .toPromise();
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = CustomizationMachineTypes.AddToBagInput & {
  deliveryDetails: DeliveryOrderDetailInput | undefined;
};

type GetAddLineItemToCartInputParams = Params & {
  product: NonNullable<Params['product']>;
};

type AddLineItemToCartParams = {
  client: Client;
  input: AddLineItemToCartInput;
};
